import { useCallback, useEffect, useState } from "react";
import { ConnectionProvider, WalletProvider, useWallet } from "@solana/wallet-adapter-react";
import { WalletModalProvider, useWalletModal } from "@solana/wallet-adapter-react-ui";
import bs58 from "bs58";
import "@solana/wallet-adapter-react-ui/styles.css";

export const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "ripp_nft_session";

export const nftFetch = async (path, opts = {}) => {
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  const sess = localStorage.getItem(SESSION_KEY);
  if (sess && !headers.Authorization) headers.Authorization = `Bearer ${sess}`;
  const r = await fetch(`${API}${path}`, { ...opts, headers });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail || data));
  return data;
};

export const adminFetch = (path, opts = {}) => nftFetch(path, { ...opts, headers: { Authorization: `Bearer ${localStorage.getItem("smc_token")}` } });

export const mediaUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${API}${u}`);

const RPC = process.env.REACT_APP_NFT_SOLANA_RPC || "https://api.devnet.solana.com";

export function WalletShell({ children }) {
  return (
    <ConnectionProvider endpoint={RPC}>
      <WalletProvider wallets={[]} autoConnect>
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

export function useRippWallet() {
  const { publicKey, signMessage, connected, disconnect } = useWallet();
  const { setVisible } = useWalletModal();
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const wallet = publicKey ? publicKey.toBase58() : null;

  useEffect(() => {
    const w = localStorage.getItem(`${SESSION_KEY}_wallet`);
    if (session && wallet && w && w !== wallet) { localStorage.removeItem(SESSION_KEY); setSession(null); }
  }, [wallet, session]);

  const signIn = useCallback(async () => {
    if (!wallet) return setVisible(true);
    if (!signMessage) return setError("this wallet cannot sign messages");
    setBusy(true); setError(null);
    try {
      const { nonce, message } = await nftFetch("/api/nft/auth/nonce", { method: "POST", body: JSON.stringify({ wallet }) });
      const sig = await signMessage(new TextEncoder().encode(message));
      const res = await nftFetch("/api/nft/auth/verify", { method: "POST", body: JSON.stringify({ wallet, nonce, signature: bs58.encode(sig) }) });
      localStorage.setItem(SESSION_KEY, res.token); localStorage.setItem(`${SESSION_KEY}_wallet`, wallet); setSession(res.token);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }, [wallet, signMessage, setVisible]);

  const signOut = useCallback(async () => { localStorage.removeItem(SESSION_KEY); setSession(null); try { await disconnect(); } catch {} }, [disconnect]);
  return { wallet, connected, session, signIn, signOut, busy, error, openWallets: () => setVisible(true) };
}
